import { useEffect } from "react";
import {
  absoluteUrl,
  buildPageTitle,
  buildSocialTitle,
  resolvePageSocialMeta,
  SITE_LOCALE,
} from "../../lib/seo";
import { SITE_DEFAULT_DESCRIPTION, SITE_NAME, SITE_OWNER } from "../../lib/siteConfig";

const MANAGED_ATTR = "data-page-meta";

/**
 * @param {string} selector
 * @param {() => HTMLElement} create
 * @param {(element: HTMLElement) => void} apply
 */
function upsertElement(selector, create, apply) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = create();
    element.setAttribute(MANAGED_ATTR, "");
    document.head.appendChild(element);
  }
  apply(element);
}

/**
 * @param {{
 *   title?: string,
 *   description?: string,
 *   path?: string,
 *   image?: string,
 *   imageAlt?: string,
 *   imageWidth?: number,
 *   imageHeight?: number,
 *   imageType?: string,
 *   twitterImage?: string,
 *   twitterImageWidth?: number,
 *   twitterImageHeight?: number,
 *   type?: "website" | "article" | "profile",
 *   noindex?: boolean,
 *   keywords?: string[],
 *   publishedTime?: string,
 *   modifiedTime?: string,
 *   author?: string,
 *   section?: string,
 *   jsonLd?: object | object[] | null,
 * }} props
 */
export default function PageMeta({
  title,
  description = SITE_DEFAULT_DESCRIPTION,
  path = "/",
  image,
  imageAlt,
  imageWidth,
  imageHeight,
  imageType,
  twitterImage,
  twitterImageWidth,
  twitterImageHeight,
  type = "website",
  noindex = false,
  keywords = [],
  publishedTime,
  modifiedTime,
  author = SITE_OWNER,
  section,
  jsonLd = null,
}) {
  useEffect(() => {
    const pageTitle = buildPageTitle(title);
    const socialTitle = buildSocialTitle(title);
    const canonicalUrl = absoluteUrl(path);
    const social = resolvePageSocialMeta({
      image,
      imageAlt,
      imageWidth,
      imageHeight,
      imageType,
      twitterImage,
      twitterImageWidth,
      twitterImageHeight,
      title,
    });
    const {
      imageUrl,
      twitterImageUrl,
      imageAlt: resolvedImageAlt,
      imageWidth: resolvedImageWidth,
      imageHeight: resolvedImageHeight,
      imageType: resolvedImageType,
      twitterImageWidth: resolvedTwitterWidth,
      twitterImageHeight: resolvedTwitterHeight,
    } = social;
    const robots = noindex
      ? "noindex, nofollow"
      : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";
    const keywordContent = keywords.filter(Boolean).join(", ");

    document.title = pageTitle;

    upsertElement(
      `meta[name="description"][${MANAGED_ATTR}]`,
      () => {
        const meta = document.createElement("meta");
        meta.setAttribute("name", "description");
        return meta;
      },
      (meta) => meta.setAttribute("content", description)
    );

    upsertElement(
      `meta[name="robots"][${MANAGED_ATTR}]`,
      () => {
        const meta = document.createElement("meta");
        meta.setAttribute("name", "robots");
        return meta;
      },
      (meta) => meta.setAttribute("content", robots)
    );

    upsertElement(
      `meta[name="author"][${MANAGED_ATTR}]`,
      () => {
        const meta = document.createElement("meta");
        meta.setAttribute("name", "author");
        return meta;
      },
      (meta) => meta.setAttribute("content", author)
    );

    if (keywordContent) {
      upsertElement(
        `meta[name="keywords"][${MANAGED_ATTR}]`,
        () => {
          const meta = document.createElement("meta");
          meta.setAttribute("name", "keywords");
          return meta;
        },
        (meta) => meta.setAttribute("content", keywordContent)
      );
    } else {
      document.head.querySelector(`meta[name="keywords"][${MANAGED_ATTR}]`)?.remove();
    }

    upsertElement(
      `link[rel="canonical"][${MANAGED_ATTR}]`,
      () => {
        const link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        return link;
      },
      (link) => link.setAttribute("href", canonicalUrl)
    );

    const openGraph = [
      ["og:site_name", SITE_NAME],
      ["og:title", socialTitle],
      ["og:description", description],
      ["og:url", canonicalUrl],
      ["og:type", type],
      ["og:locale", SITE_LOCALE],
      ["og:image", imageUrl],
      ["og:image:secure_url", imageUrl.startsWith("https://") ? imageUrl : ""],
      ["og:image:alt", resolvedImageAlt],
      ...(resolvedImageType ? [["og:image:type", resolvedImageType]] : []),
      ...(resolvedImageWidth ? [["og:image:width", String(resolvedImageWidth)]] : []),
      ...(resolvedImageHeight ? [["og:image:height", String(resolvedImageHeight)]] : []),
    ].filter(([, content]) => content);

    for (const [property, content] of openGraph) {
      upsertElement(
        `meta[property="${property}"][${MANAGED_ATTR}]`,
        () => {
          const meta = document.createElement("meta");
          meta.setAttribute("property", property);
          return meta;
        },
        (meta) => meta.setAttribute("content", content)
      );
    }

    const twitterTags = [
      ["twitter:card", "summary_large_image"],
      ["twitter:title", socialTitle],
      ["twitter:description", description],
      ["twitter:image", twitterImageUrl],
      ["twitter:image:alt", resolvedImageAlt],
      ...(resolvedTwitterWidth ? [["twitter:image:width", String(resolvedTwitterWidth)]] : []),
      ...(resolvedTwitterHeight ? [["twitter:image:height", String(resolvedTwitterHeight)]] : []),
    ];

    for (const [name, content] of twitterTags) {
      upsertElement(
        `meta[name="${name}"][${MANAGED_ATTR}]`,
        () => {
          const meta = document.createElement("meta");
          meta.setAttribute("name", name);
          return meta;
        },
        (meta) => meta.setAttribute("content", content)
      );
    }

    if (type === "article") {
      const articleTags = [
        ["article:author", author],
        ...(section ? [["article:section", section]] : []),
        ...(publishedTime ? [["article:published_time", publishedTime]] : []),
        ...(modifiedTime ? [["article:modified_time", modifiedTime]] : []),
      ];

      for (const [property, content] of articleTags) {
        upsertElement(
          `meta[property="${property}"][${MANAGED_ATTR}]`,
          () => {
            const meta = document.createElement("meta");
            meta.setAttribute("property", property);
            return meta;
          },
          (meta) => meta.setAttribute("content", content)
        );
      }
    }

    const schemas = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
    const existingJsonLd = document.head.querySelector(`script#page-meta-jsonld[${MANAGED_ATTR}]`);
    if (existingJsonLd) {
      existingJsonLd.remove();
    }

    if (schemas.length > 0) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "page-meta-jsonld";
      script.setAttribute(MANAGED_ATTR, "");
      script.textContent = JSON.stringify(schemas.length === 1 ? schemas[0] : schemas);
      document.head.appendChild(script);
    }

    return () => {
      document.head.querySelectorAll(`[${MANAGED_ATTR}]`).forEach((node) => {
        if (node.id === "page-meta-jsonld" || node.tagName === "META" || node.tagName === "LINK") {
          node.remove();
        }
      });
    };
  }, [
    author,
    description,
    image,
    imageAlt,
    imageHeight,
    imageType,
    imageWidth,
    jsonLd,
    keywords,
    modifiedTime,
    noindex,
    path,
    publishedTime,
    section,
    title,
    twitterImage,
    twitterImageHeight,
    twitterImageWidth,
    type,
  ]);

  return null;
}
